/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  type ColorsInput,
  type DarkThemeColorsInput,
  type LightThemeColorsInput,
  type MultiThemeColorsInput,
  type SingleThemeColorsInput,
  type StormWorkspaceConfig,
  COLOR_KEYS,
  STORM_DEFAULT_DOCS,
  STORM_DEFAULT_HOMEPAGE,
  STORM_DEFAULT_LICENSING,
  VariantInput
} from "@storm-software/config";
import { getLogLevelLabel } from "../logger/get-log-level";
import type { DeepPartial, LogLevelLabel } from "../types";
import { correctPaths } from "../utilities/correct-paths";

/**
 * Get the config for an extension module of Storm workspace from environment variables
 *
 * @param extensionName - The name of the extension module
 * @returns The config for the specified Storm extension module. If the module does not exist, `undefined` is returned.
 */
export const getExtensionEnv = <
  TConfig extends Record<string, any> = Record<string, any>
>(
  extensionName: string
): TConfig | undefined => {
  const prefix = `STORM_EXTENSION_${extensionName.toUpperCase()}_`;
  return Object.keys(process.env)
    .filter(key => key.startsWith(prefix))
    .reduce((ret: Record<string, any>, key: string) => {
      const name = key
        .replace(prefix, "")
        .split("_")
        .map(i =>
          i.length > 0
            ? i.trim().charAt(0).toUpperCase() + i.trim().slice(1)
            : ""
        )
        .join("");
      if (name) {
        ret[name] = process.env[key];
      }

      return ret;
    }, {}) as TConfig;
};

/**
 * Get the config for the current Storm workspace
 *
 * @returns The config for the current Storm workspace from environment variables
 */
export const getConfigEnv = (): DeepPartial<StormWorkspaceConfig> => {
  const prefix = "STORM_";

  let config: DeepPartial<StormWorkspaceConfig> = {
    extends: process.env[`${prefix}EXTENDS`] || undefined,
    name: process.env[`${prefix}NAME`] || undefined,
    variant: (process.env[`${prefix}VARIANT`] as VariantInput) || undefined,
    namespace: process.env[`${prefix}NAMESPACE`] || undefined,
    owner: process.env[`${prefix}OWNER`] || undefined,
    bot: {
      name: process.env[`${prefix}BOT_NAME`] || undefined,
      email: process.env[`${prefix}BOT_EMAIL`] || undefined
    },
    release: {
      banner: {
        url: process.env[`${prefix}RELEASE_BANNER_URL`] || undefined,
        alt: (process.env[`${prefix}RELEASE_BANNER_ALT`] || undefined) as string
      },
      header: process.env[`${prefix}RELEASE_HEADER`] || undefined,
      footer: process.env[`${prefix}RELEASE_FOOTER`] || undefined
    },
    error: {
      codesFile: process.env[`${prefix}ERROR_CODES_FILE`] || undefined,
      url: process.env[`${prefix}ERROR_URL`] || undefined
    },
    socials: {
      twitter: process.env[`${prefix}SOCIAL_TWITTER`] || undefined,
      discord: process.env[`${prefix}SOCIAL_DISCORD`] || undefined,
      telegram: process.env[`${prefix}SOCIAL_TELEGRAM`] || undefined,
      slack: process.env[`${prefix}SOCIAL_SLACK`] || undefined,
      medium: process.env[`${prefix}SOCIAL_MEDIUM`] || undefined,
      github: process.env[`${prefix}SOCIAL_GITHUB`] || undefined
    },
    organization:
      process.env[`${prefix}ORG`] ||
      process.env[`${prefix}ORGANIZATION`] ||
      process.env[`${prefix}ORG_NAME`] ||
      process.env[`${prefix}ORGANIZATION_NAME`]
        ? process.env[`${prefix}ORG_DESCRIPTION`] ||
          process.env[`${prefix}ORGANIZATION_DESCRIPTION`] ||
          process.env[`${prefix}ORG_URL`] ||
          process.env[`${prefix}ORGANIZATION_URL`] ||
          process.env[`${prefix}ORG_LOGO`] ||
          process.env[`${prefix}ORGANIZATION_LOGO`]
          ? {
              name: (process.env[`${prefix}ORG`] ||
                process.env[`${prefix}ORGANIZATION`] ||
                process.env[`${prefix}ORG_NAME`] ||
                process.env[`${prefix}ORGANIZATION_NAME`])!,
              description:
                process.env[`${prefix}ORG_DESCRIPTION`] ||
                process.env[`${prefix}ORGANIZATION_DESCRIPTION`] ||
                undefined,
              url:
                process.env[`${prefix}ORG_URL`] ||
                process.env[`${prefix}ORGANIZATION_URL`] ||
                undefined,
              logo:
                process.env[`${prefix}ORG_LOGO`] ||
                process.env[`${prefix}ORGANIZATION_LOGO`] ||
                undefined,
              icon:
                process.env[`${prefix}ORG_ICON`] ||
                process.env[`${prefix}ORGANIZATION_ICON`] ||
                undefined
            }
          : process.env[`${prefix}ORG`] ||
            process.env[`${prefix}ORGANIZATION`] ||
            process.env[`${prefix}ORG_NAME`] ||
            process.env[`${prefix}ORGANIZATION_NAME`]
        : undefined,
    packageManager:
      (process.env[
        `${prefix}PACKAGE_MANAGER`
      ] as StormWorkspaceConfig["packageManager"]) || undefined,
    license: process.env[`${prefix}LICENSE`] || undefined,
    homepage: process.env[`${prefix}HOMEPAGE`] || undefined,
    docs: process.env[`${prefix}DOCS`] || undefined,
    portal: process.env[`${prefix}PORTAL`] || undefined,
    licensing: process.env[`${prefix}LICENSING`] || undefined,
    contact: process.env[`${prefix}CONTACT`] || undefined,
    support: process.env[`${prefix}SUPPORT`] || undefined,
    timezone: process.env[`${prefix}TIMEZONE`] || process.env.TZ || undefined,
    locale: process.env[`${prefix}LOCALE`] || process.env.LOCALE || undefined,
    configFile: process.env[`${prefix}WORKSPACE_CONFIG_FILE`]
      ? correctPaths(process.env[`${prefix}WORKSPACE_CONFIG_FILE`])
      : undefined,
    workspaceRoot: process.env[`${prefix}WORKSPACE_ROOT`]
      ? correctPaths(process.env[`${prefix}WORKSPACE_ROOT`])
      : undefined,
    directories: {
      cache: process.env[`${prefix}CACHE_DIR`]
        ? correctPaths(process.env[`${prefix}CACHE_DIR`])
        : process.env[`${prefix}CACHE_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}CACHE_DIRECTORY`])
          : undefined,
      data: process.env[`${prefix}DATA_DIR`]
        ? correctPaths(process.env[`${prefix}DATA_DIR`])
        : process.env[`${prefix}DATA_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}DATA_DIRECTORY`])
          : undefined,
      config: process.env[`${prefix}CONFIG_DIR`]
        ? correctPaths(process.env[`${prefix}CONFIG_DIR`])
        : process.env[`${prefix}CONFIG_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}CONFIG_DIRECTORY`])
          : undefined,
      temp: process.env[`${prefix}TEMP_DIR`]
        ? correctPaths(process.env[`${prefix}TEMP_DIR`])
        : process.env[`${prefix}TEMP_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}TEMP_DIRECTORY`])
          : undefined,
      log: process.env[`${prefix}LOG_DIR`]
        ? correctPaths(process.env[`${prefix}LOG_DIR`])
        : process.env[`${prefix}LOG_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}LOG_DIRECTORY`])
          : undefined,
      build: process.env[`${prefix}BUILD_DIR`]
        ? correctPaths(process.env[`${prefix}BUILD_DIR`])
        : process.env[`${prefix}BUILD_DIRECTORY`]
          ? correctPaths(process.env[`${prefix}BUILD_DIRECTORY`])
          : undefined
    },
    skipCache:
      process.env[`${prefix}SKIP_CACHE`] !== undefined
        ? Boolean(process.env[`${prefix}SKIP_CACHE`])
        : undefined,
    mode:
      ((process.env[`${prefix}MODE`] ??
        process.env.NODE_ENV ??
        process.env.ENVIRONMENT) as StormWorkspaceConfig["mode"]) || undefined,
    // ci:
    //   process.env[`${prefix}CI`] !== undefined
    //     ? Boolean(
    //         process.env[`${prefix}CI`] ??
    //           process.env.CI ??
    //           process.env.CONTINUOUS_INTEGRATION
    //       )
    //     : undefined,

    repository: process.env[`${prefix}REPOSITORY`] || undefined,
    branch: process.env[`${prefix}BRANCH`] || undefined,
    preid: process.env[`${prefix}PRE_ID`] || undefined,
    registry: {
      github: process.env[`${prefix}REGISTRY_GITHUB`] || undefined,
      npm: process.env[`${prefix}REGISTRY_NPM`] || undefined,
      cargo: process.env[`${prefix}REGISTRY_CARGO`] || undefined,
      cyclone: process.env[`${prefix}REGISTRY_CYCLONE`] || undefined,
      container: process.env[`${prefix}REGISTRY_CONTAINER`] || undefined
    },
    logLevel:
      process.env[`${prefix}LOG_LEVEL`] !== null &&
      process.env[`${prefix}LOG_LEVEL`] !== undefined
        ? process.env[`${prefix}LOG_LEVEL`] &&
          Number.isSafeInteger(
            Number.parseInt(process.env[`${prefix}LOG_LEVEL`] as string)
          )
          ? getLogLevelLabel(
              Number.parseInt(process.env[`${prefix}LOG_LEVEL`] as string)
            )
          : (process.env[`${prefix}LOG_LEVEL`] as LogLevelLabel)
        : undefined,
    skipConfigLogging:
      process.env[`${prefix}SKIP_CONFIG_LOGGING`] !== undefined
        ? Boolean(process.env[`${prefix}SKIP_CONFIG_LOGGING`])
        : undefined
  };

  const themeNames = Object.keys(process.env).filter(
    envKey =>
      envKey.startsWith(`${prefix}COLOR_`) &&
      COLOR_KEYS.every(
        colorKey =>
          !envKey.startsWith(`${prefix}COLOR_LIGHT_${colorKey}`) &&
          !envKey.startsWith(`${prefix}COLOR_DARK_${colorKey}`)
      )
  );

  config.colors =
    themeNames.length > 0
      ? themeNames.reduce(
          (ret: Record<string, ColorsInput>, themeName: string) => {
            ret[themeName] = getThemeColorsEnv(prefix, themeName);

            return ret;
          },
          {}
        )
      : getThemeColorsEnv(prefix);

  if (config.docs === STORM_DEFAULT_DOCS) {
    if (config.homepage === STORM_DEFAULT_HOMEPAGE) {
      config.docs = `${STORM_DEFAULT_HOMEPAGE}/projects/${config.name}/docs`;
    } else {
      config.docs = `${config.homepage}/docs`;
    }
  }

  if (config.licensing === STORM_DEFAULT_LICENSING) {
    if (config.homepage === STORM_DEFAULT_HOMEPAGE) {
      config.licensing = `${STORM_DEFAULT_HOMEPAGE}/projects/${config.name}/licensing`;
    } else {
      config.licensing = `${config.homepage}/docs`;
    }
  }

  const serializedConfig = process.env[`${prefix}WORKSPACE_CONFIG`];
  if (serializedConfig) {
    const parsed = JSON.parse(serializedConfig);
    config = {
      ...config,
      ...parsed,
      colors: { ...config.colors, ...parsed.colors },
      extensions: { ...config.extensions, ...parsed.extensions }
    };
  }

  return config;

  /*const extensionPrefix = `${prefix}EXTENSION_`;
  return Object.keys(process.env)
    .filter((key) => key.startsWith(extensionPrefix))
    .reduce((ret: StormWorkspaceConfig, key: string) => {
      const extensionName = key
        .substring(prefix.length, key.indexOf("_", prefix.length))
        .split("_")
        .map((i) => (i.length > 0 ? i.trim().charAt(0).toUpperCase() + i.trim().slice(1) : ""))
        .join("");
      if (extensionName) {
        ret.extensions[extensionName] = getExtensionEnv(extensionName);
      }

      return ret;
    }, config);*/
};

const getThemeColorsEnv = (prefix: string, theme?: string): ColorsInput => {
  const themeName =
    `COLOR_${theme && theme !== "base" ? `${theme}_` : ""}`.toUpperCase();

  return process.env[`${prefix}${themeName}LIGHT_BRAND`] ||
    process.env[`${prefix}${themeName}DARK_BRAND`]
    ? getMultiThemeColorsEnv(prefix + themeName)
    : getSingleThemeColorsEnv(prefix + themeName);
};

const getSingleThemeColorsEnv = (prefix: string): SingleThemeColorsInput => {
  const gradient = [] as string[];
  if (
    process.env[`${prefix}GRADIENT_START`] &&
    process.env[`${prefix}GRADIENT_END`]
  ) {
    gradient.push(
      process.env[`${prefix}GRADIENT_START`]!,
      process.env[`${prefix}GRADIENT_END`]!
    );
  } else if (
    process.env[`${prefix}GRADIENT_0`] ||
    process.env[`${prefix}GRADIENT_1`]
  ) {
    let index = process.env[`${prefix}GRADIENT_0`] ? 0 : 1;
    while (process.env[`${prefix}GRADIENT_${index}`]) {
      gradient.push(process.env[`${prefix}GRADIENT_${index}`]!);
      index++;
    }
  }

  return {
    dark: process.env[`${prefix}DARK`],
    light: process.env[`${prefix}LIGHT`],
    brand: process.env[`${prefix}BRAND`],
    alternate: process.env[`${prefix}ALTERNATE`],
    accent: process.env[`${prefix}ACCENT`],
    link: process.env[`${prefix}LINK`],
    help: process.env[`${prefix}HELP`],
    success: process.env[`${prefix}SUCCESS`],
    info: process.env[`${prefix}INFO`],
    debug: process.env[`${prefix}DEBUG`],
    warning: process.env[`${prefix}WARNING`],
    danger: process.env[`${prefix}DANGER`],
    fatal: process.env[`${prefix}FATAL`],
    positive: process.env[`${prefix}POSITIVE`],
    negative: process.env[`${prefix}NEGATIVE`],
    gradient
  };
};

const getMultiThemeColorsEnv = (prefix: string): MultiThemeColorsInput => {
  return {
    light: getBaseThemeColorsEnv<"light", typeof prefix>(`${prefix}_LIGHT_`),
    dark: getBaseThemeColorsEnv<"dark", typeof prefix>(`${prefix}_DARK_`)
  };
};

type ColorThemeTypes = "dark" | "light";

const getBaseThemeColorsEnv = <
  TColorThemeType extends ColorThemeTypes = ColorThemeTypes,
  TExistingPrefix extends string = string,
  TResult = TColorThemeType extends "dark"
    ? DarkThemeColorsInput
    : LightThemeColorsInput
>(
  prefix: `${TExistingPrefix}_${Uppercase<TColorThemeType>}_`
): TResult => {
  const gradient = [] as string[];
  if (
    process.env[`${prefix}GRADIENT_START`] &&
    process.env[`${prefix}GRADIENT_END`]
  ) {
    gradient.push(
      process.env[`${prefix}GRADIENT_START`]!,
      process.env[`${prefix}GRADIENT_END`]!
    );
  } else if (
    process.env[`${prefix}GRADIENT_0`] ||
    process.env[`${prefix}GRADIENT_1`]
  ) {
    let index = process.env[`${prefix}GRADIENT_0`] ? 0 : 1;
    while (process.env[`${prefix}GRADIENT_${index}`]) {
      gradient.push(process.env[`${prefix}GRADIENT_${index}`]!);
      index++;
    }
  }

  return {
    foreground: process.env[`${prefix}FOREGROUND`],
    background: process.env[`${prefix}BACKGROUND`],
    brand: process.env[`${prefix}BRAND`],
    alternate: process.env[`${prefix}ALTERNATE`],
    accent: process.env[`${prefix}ACCENT`],
    link: process.env[`${prefix}LINK`],
    help: process.env[`${prefix}HELP`],
    success: process.env[`${prefix}SUCCESS`],
    info: process.env[`${prefix}INFO`],
    debug: process.env[`${prefix}DEBUG`],
    warning: process.env[`${prefix}WARNING`],
    danger: process.env[`${prefix}DANGER`],
    fatal: process.env[`${prefix}FATAL`],
    positive: process.env[`${prefix}POSITIVE`],
    negative: process.env[`${prefix}NEGATIVE`],
    gradient
  } as TResult;
};
